import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { eventInfo, images } from '../data/mock';
import { ChevronDown, Sparkles, Ticket } from 'lucide-react';

// Fun particle colors - tropical vibes matching new logo
const PARTICLE_COLORS = [
  'bg-pink-400', 'bg-orange-400', 'bg-yellow-400',
  'bg-red-400', 'bg-fuchsia-400', 'bg-amber-400',
  'bg-rose-400', 'bg-purple-400'
];

// Stable across renders (react-hooks/purity)
const HERO_PARTICLES = Array.from({ length: 40 }, (_, i) => {
  const size = 4 + Math.random() * 12;
  const dur = 8 + Math.random() * 12;
  return {
    key: i,
    color: PARTICLE_COLORS[i % PARTICLE_COLORS.length],
    style: {
      width: `${size}px`,
      height: `${size}px`,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${dur}s`,
      animation: `float ${dur}s ease-in-out infinite`,
      filter: 'blur(1px)'
    }
  };
});

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTickets = () => {
    document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth' });
  };

  const openTickets = () => {
    if (eventInfo.ticketUrl) {
      window.open(eventInfo.ticketUrl, '_blank');
    } else {
      scrollToTickets();
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Parallax */}
      <div 
        className="absolute inset-0 z-0"
        style={{ transform: `translateY(${scrollY * 0.5}px)` }}
      >
        <img
          src={images.hero}
          alt="Diamond Head Waikiki"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
      </div>

      {/* Animated colorful particles */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {HERO_PARTICLES.map((p) => (
          <div
            key={p.key}
            className={`absolute rounded-full ${p.color} opacity-60`}
            style={p.style}
          />
        ))}
      </div>

      {/* Decorative shapes - matching logo colors */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-pink-500/20 to-fuchsia-500/20 rounded-full blur-2xl animate-pulse hidden sm:block" />
      <div className="absolute bottom-40 right-10 w-40 h-40 bg-gradient-to-br from-orange-500/20 to-yellow-500/20 rounded-full blur-2xl animate-pulse hidden sm:block" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/3 right-20 w-24 h-24 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full blur-2xl animate-pulse hidden sm:block" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        {/* New Image Logo */}
        <div className="mb-6 sm:mb-8 animate-bounceIn">
          <img 
            src={images.logo} 
            alt="Aloha Tequila & Arts Festival" 
            className="h-36 sm:h-48 md:h-64 lg:h-80 mx-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
            style={{ filter: 'drop-shadow(0 0 40px rgba(236, 72, 153, 0.4))' }}
          />
        </div>

        {/* Fun Badge */}
        <div className="mb-6 animate-fadeInUp animation-delay-200">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500/20 to-orange-500/20 backdrop-blur-md px-6 py-3 rounded-full border border-pink-500/30 hover-lift cursor-default">
            <Sparkles className="w-5 h-5 text-pink-400 animate-pulse" />
            <span className="text-white font-bold tracking-widest text-sm">{eventInfo.edition?.toUpperCase() || '1ST ANNUAL'}</span>
            <Sparkles className="w-5 h-5 text-orange-400 animate-pulse" />
          </div>
        </div>

        {/* Tagline */}
        <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 animate-fadeInUp animation-delay-300 font-medium max-w-3xl mx-auto px-2">
          {eventInfo.tagline}
        </p>

        {/* Date & Location */}
        <div className="flex flex-col items-center gap-3 mb-8 sm:mb-10 animate-fadeInUp animation-delay-400">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
            <span className="bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold px-4 sm:px-6 py-2 rounded-full text-base sm:text-lg shadow-lg animate-pulse-glow">
              {eventInfo.date}
            </span>
            <span className="bg-white/10 backdrop-blur-sm text-white font-semibold px-4 sm:px-6 py-2 rounded-full text-base sm:text-lg border border-white/20">
              {eventInfo.time}
            </span>
          </div>
          <p className="text-white/80 text-sm sm:text-lg text-center px-4">
            📍 {eventInfo.location} • {eventInfo.address}
          </p>
        </div>

        {/* CTA Button - Links to TicketSpice */}
        <div className="animate-fadeInUp animation-delay-500 px-4">
          <Button 
            onClick={openTickets}
            className="bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 hover:from-pink-600 hover:via-orange-600 hover:to-yellow-600 text-white text-lg sm:text-xl px-8 sm:px-14 py-5 sm:py-7 h-auto rounded-full font-bold shadow-2xl hover:shadow-pink-500/50 transition-all duration-300 transform hover:scale-110 animate-gradient group w-full sm:w-auto"
          >
            <span className="flex items-center justify-center gap-2 sm:gap-3">
              <Ticket className="w-5 h-5 sm:w-6 sm:h-6 group-hover:animate-bounce" />
              GET TICKETS NOW
              <Ticket className="w-5 h-5 sm:w-6 sm:h-6 group-hover:animate-bounce hidden sm:block" />
            </span>
          </Button>

          {/* Urgency / scarcity message */}
          <div
            className="mt-4 inline-flex items-center gap-2 bg-red-500/15 border border-red-400/40 backdrop-blur-sm rounded-full px-4 py-2 animate-pulse-glow"
            data-testid="hero-urgency-message"
          >
            <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
            <span className="text-white text-sm sm:text-base font-bold">
              <span className="text-yellow-300">Tickets will sell out</span> — don&apos;t delay!
            </span>
            <Sparkles className="w-4 h-4 text-yellow-300 animate-pulse" />
          </div>

          <p className="text-pink-300/80 text-sm mt-3 font-medium">Secure checkout via TicketSpice</p>
        </div>

        {/* Fun emoji bar */}
        <div className="mt-8 animate-fadeInUp animation-delay-600">
          <p className="text-2xl animate-wave inline-block">🌺🍹🎨🎶🥃🎉</p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <div className="flex flex-col items-center gap-2">
          <span className="text-white/60 text-sm font-medium">Scroll to explore</span>
          <ChevronDown className="w-8 h-8 text-pink-400" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
